import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, LogOut, Package, X } from "lucide-react";
import { toast } from "sonner";
import api, { inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";

const STEPS = ["Placed", "Packed", "Shipped", "Delivered"];

export default function MyOrdersModal() {
  const { ordersOpen, setOrdersOpen, customer, setCustomer } = useStore();
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (ordersOpen) {
      setOrders(null);
      api.get("/my/orders").then((r) => setOrders(r.data.orders)).catch(() => setOrders([]));
    }
  }, [ordersOpen]);

  const logout = async () => {
    await api.post("/auth/customer/logout").catch(() => {});
    localStorage.removeItem("lj_customer_token");
    setCustomer(null);
    setOrdersOpen(false);
    toast.success("Logged out. See you soon!");
  };

  return (
    <AnimatePresence>
      {ordersOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[80] bg-ink/50 backdrop-blur-sm" onClick={() => setOrdersOpen(false)} />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 bottom-0 z-[90] w-full max-w-md bg-cream flex flex-col shadow-2xl"
            data-testid="my-orders-modal"
          >
            <div className="flex items-center justify-between p-6 border-b border-ink/10">
              <div>
                <h3 className="font-serif font-bold text-2xl text-ink">My Orders</h3>
                <p className="font-mono text-[10px] tracking-widest uppercase text-charcoal/60 mt-1">
                  {customer?.name} · +91 {customer?.phone}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={logout} className="rounded-full p-2 hover:bg-blush text-charcoal/60 hover:text-terracotta transition-colors" data-testid="customer-logout-btn" aria-label="Logout" title="Logout">
                  <LogOut className="w-5 h-5" />
                </button>
                <button onClick={() => setOrdersOpen(false)} className="rounded-full p-2 hover:bg-blush transition-colors" data-testid="my-orders-close" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4" data-testid="my-orders-list">
              {orders === null ? (
                <div className="flex justify-center pt-16"><Loader2 className="w-7 h-7 animate-spin text-terracotta" /></div>
              ) : orders.length === 0 ? (
                <div className="text-center pt-16">
                  <Package className="w-10 h-10 text-ink/20 mx-auto mb-4" />
                  <p className="font-serif italic text-xl text-charcoal">No orders on this number yet.</p>
                  <p className="text-xs text-charcoal/60 mt-2">Orders placed with +91 {customer?.phone} appear here automatically.</p>
                </div>
              ) : (
                orders.map((o) => {
                  const stepIdx = STEPS.indexOf(o.status);
                  return (
                    <div key={o.id} className="bg-white rounded-2xl p-5" data-testid={`my-order-${o.id}`}>
                      <div className="flex items-center justify-between">
                        <span className="font-mono font-bold text-sm text-ink">{o.id}</span>
                        <span className={`font-mono text-[10px] uppercase tracking-widest rounded-full px-3 py-1 ${o.status === "Cancelled" || o.payment_status === "failed" ? "bg-red-100 text-red-600" : o.status === "Awaiting Payment" ? "bg-amber-100 text-amber-700" : "bg-mint text-sage-dark"}`}>
                          {o.payment_status === "failed" ? "Payment Failed" : o.status}
                        </span>
                      </div>
                      {STEPS.includes(o.status) && (
                        <div className="flex gap-1 mt-3">
                          {STEPS.map((s, i) => (
                            <span key={s} className={`h-1.5 flex-1 rounded-full ${i <= stepIdx ? "bg-terracotta" : "bg-ink/10"}`} title={s} />
                          ))}
                        </div>
                      )}
                      <div className="mt-3 space-y-1">
                        {o.items.map((i) => (
                          <p key={i.product_id + i.size} className="text-xs text-charcoal">{i.name} · {i.size} · x{i.qty}</p>
                        ))}
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-ink/5">
                        <span className="font-mono text-[10px] text-charcoal/60">
                          {new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                        </span>
                        <span className="font-serif font-bold text-terracotta">{inr(o.total)}</span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
